import { useEffect, useState } from "react";
import PageContainer from "../../../components/page-container";
import Button from "../../../components/button/button";
import Message from "../../../components/toast-message";
import SelectInput from "../../../components/forms/select";
import { buildAgentNavigation } from "../../../consts/agent-nav-config";
import { useAgentConfigSource } from "../../../helpers/hooks/agent-config-source";
import { modelsByProvider } from "../../agent-manage/llm-settings";
import LLMSettings from "../../agent-manage/llm-settings";
import SubAgentMissing from "../sub-agent-scope/sub-agent-missing";

const INHERIT = "";

/**
 * LLM tab.
 *
 * At app level this is the existing LLM settings page (provider, model, API key
 * and key validation). A sub-agent has no credentials of its own — it only
 * picks an optional provider/model override and otherwise inherits the agent's
 * settings — so the sub-agent scope gets the narrower form below.
 */
export default function AgentLlm() {
  const {
    subAgentId,
    subAgent,
    isSubAgent,
    scopeLoading,
    scopeError,
    llm: llmSource,
  } = useAgentConfigSource();

  const [provider, setProvider] = useState(INHERIT);
  const [model, setModel] = useState(INHERIT);
  const [loaded, setLoaded] = useState(false);
  const [saveState, setSaveState] = useState({
    processing: false,
    success: false,
    failed: false,
  });

  useEffect(() => {
    if (!isSubAgent || scopeLoading) return;
    let cancelled = false;
    (async () => {
      const llm = (await llmSource.get()) || {};
      if (cancelled) return;
      setProvider(llm.provider || INHERIT);
      setModel(llm.model || INHERIT);
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubAgent, subAgentId, scopeLoading]);

  // app scope keeps the full existing settings page, credentials and all
  if (!isSubAgent) return <LLMSettings navigationPosition="below-header" />;

  const models = provider ? modelsByProvider[provider] || [] : [];

  const handleProviderChange = (e) => {
    setProvider(e.target.value);
    // the model list is provider-specific, so a stale choice must not survive
    setModel(INHERIT);
  };

  const handleSave = async () => {
    setSaveState({ processing: true, success: false, failed: false });
    // no provider means "inherit", which is stored as no override at all
    const llm = provider ? { provider, ...(model ? { model } : {}) } : {};
    const { status } = await llmSource.save(llm);
    const ok = status === 200 || status === 201;
    setSaveState({ processing: false, success: ok, failed: !ok });
  };

  const navigation = buildAgentNavigation({ subAgentId });

  if (!scopeLoading && scopeError) {
    return <SubAgentMissing navigation={navigation} />;
  }

  return (
    <PageContainer
      title="LLM"
      subtitle={`Model used by ${subAgent?.name || "this sub-agent"}`}
      navigationPosition="below-header"
      navigation={navigation}
      headerAction={
        <Button
          text="Save"
          onClick={handleSave}
          loading={saveState.processing}
          disabled={scopeLoading || saveState.processing}
        />
      }
    >
      {saveState.success && (
        <div className="mb-4">
          <Message text="LLM settings saved." type="success" />
        </div>
      )}
      {saveState.failed && (
        <div className="mb-4">
          <Message text="Failed to save. Please try again." type="failure" />
        </div>
      )}

      {scopeLoading || !loaded ? (
        <div className="flex justify-center items-center py-16">
          <p className="text-sm text-gray-400">Loading…</p>
        </div>
      ) : (
        <div className="mt-6 px-1 flex flex-col gap-4">
          <p className="text-xs text-gray-500">
            Leave the provider unset to inherit the agent's LLM settings. API
            keys always come from the app-level configuration.
          </p>

          <SelectInput
            rootClass="w-1/2"
            label="LLM Provider"
            id="sub_agent_llm_provider"
            name="provider"
            value={provider}
            onChange={handleProviderChange}
            optionsComp={
              <>
                <option value={INHERIT}>Inherit from agent</option>
                <option value="openai">OpenAI</option>
                <option value="gemini">Gemini</option>
              </>
            }
          />

          {provider && (
            <SelectInput
              rootClass="w-1/2"
              label="Model"
              id="sub_agent_llm_model"
              name="model"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              optionsComp={
                <>
                  <option value={INHERIT}>Inherit from agent</option>
                  {models.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.name}
                    </option>
                  ))}
                </>
              }
            />
          )}
        </div>
      )}
    </PageContainer>
  );
}
